/* eslint-disable no-template-curly-in-string */
import useAppStore from "@/context/store";
import { useSafeState } from "ahooks";
import classNames from "classnames";
import { useEffect, useMemo, useRef } from "react";
import { When } from "react-if";
import HBIcon from "../HBIcon";
import styles from "./index.module.less";

const WIDTH = 700;
const HEIGHT = 32;
const MARGIN_BOTTOM = 20;

export default function HBCategorySelect({
  onChange,
  value,
  options,
  allText = "最新",
  hideAll = false,
  filterValues = [], // 过滤掉不需要的分类
  baseHref,
  minWidth,
  maxWidth,
}) {
  const { wrapperWidth, categories: appCategories } = useAppStore(
    ({ wrapperWidth, categories }) => ({
      wrapperWidth,
      categories,
    })
  );
  const containerRef = useRef(null);
  const firstRef = useRef(null);
  const heightRef = useRef(0);
  const [width, setWidth] = useSafeState(WIDTH);
  const [right, setRight] = useSafeState(0);
  const [height, setHeight] = useSafeState(HEIGHT);
  const [showAll, setShowAll] = useSafeState(false);
  const [activeOption, setActiveOption] = useSafeState(null);
  const [showToggle, setShowToggle] = useSafeState(false);

  const categories = useMemo(() => {
    let categories = options || appCategories;
    if (filterValues.length > 0) {
      categories = categories.filter(
        (category) => !filterValues.includes(category.value)
      );
    }
    return categories;
  }, [options, appCategories, filterValues]);

  useEffect(() => {
    if (value === null) {
      setActiveOption(null);
    } else if (value && categories.length > 0) {
      const activeOption = categories.find((option) => option.value === value);
      setActiveOption(activeOption);
    } else {
      setActiveOption(undefined);
    }
  }, [value, categories, setActiveOption]);

  useEffect(() => {
    // 这个取计算dom的值需要些延迟
    setTimeout(() => {
      const height = containerRef.current?.offsetHeight - MARGIN_BOTTOM;
      const right = firstRef.current?.offsetLeft;
      heightRef.current = height;
      setHeight(showAll ? height : HEIGHT);
      setRight(right || 0);
      setShowToggle(height !== HEIGHT);
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, showToggle, width]);

  useEffect(() => {
    if (wrapperWidth <= 1032) {
      setWidth(minWidth || 400);
    } else {
      setWidth(maxWidth || WIDTH);
    }
  }, [maxWidth, minWidth, setWidth, wrapperWidth]);

  const onClick = (evt, option) => {
    evt.preventDefault();
    setActiveOption(option);
    onChange && onChange(option);
  };

  const onToggle = () => {
    setShowAll(!showAll);
    if (!showAll) {
      setHeight(heightRef.current);
    } else {
      setHeight(HEIGHT);
    }
  };
  const renderChildren = (
    <div style={{ width, height }} className={styles.container}>
      <When condition={categories.length > 0 && !hideAll}>
        <a
          data-gd-click="button_click"
          data-button-name={allText}
          className={classNames([
            styles.item,
            !activeOption ? styles.active : null,
          ])}
          {...(!activeOption ? { id: "active-filter-item" } : {})}
          {...(baseHref
            ? { href: baseHref.replace("/${categoryId}", "") }
            : null)}
          onClick={(evt) => onClick(evt, null)}
        >
          {allText}
        </a>
      </When>
      {categories.map((option) => (
        <a
          key={option.value}
          data-gd-click="button_click"
          data-button-name={option.name || option.label}
          className={classNames([
            styles.item,
            activeOption?.value === option.value ? styles.active : null,
          ])}
          // 埋点取数据用
          {...(activeOption?.value === option.value
            ? { id: "active-filter-item" }
            : {})}
          {...(baseHref
            ? { href: baseHref.replace("${categoryId}", option.value) }
            : null)}
          onClick={(evt) => onClick(evt, option)}
        >
          {option.name || option.label}
        </a>
      ))}
      <When condition={showToggle && categories.length > 0}>
        <div
          className={classNames([styles.item, styles.up])}
          onClick={onToggle}
        >
          <HBIcon type="ic_fold" />
        </div>
      </When>
    </div>
  );

  const computedChildren = (
    <div
      style={{ width }}
      className={classNames([styles.container, styles.computed])}
      ref={containerRef}
    >
      <When condition={categories.length > 0 && !hideAll}>
        <a
          ref={firstRef}
          className={classNames([
            styles.item,
            !activeOption ? styles.active : null,
          ])}
          {...(!activeOption ? { id: "active-filter-item" } : {})}
          {...(baseHref
            ? { href: baseHref.replace("/${categoryId}", "") }
            : null)}
          onClick={(evt) => onClick(evt, null)}
        >
          {allText}
        </a>
      </When>
      {categories.map((option) => (
        <a
          key={option.value}
          className={classNames([
            styles.item,
            activeOption?.value === option.value ? styles.active : null,
          ])}
          {...(activeOption?.value === option.value
            ? { id: "active-filter-item" }
            : {})}
          {...(baseHref
            ? { href: baseHref.replace("${categoryId}", option.value) }
            : null)}
          onClick={(evt) => onClick(evt, option)}
        >
          {option.name || option.label}
        </a>
      ))}
      <When condition={showToggle && categories.length > 0}>
        <div className={classNames([styles.item, styles.up])}>
          <HBIcon type="ic_fold" />
        </div>
      </When>
    </div>
  );

  return (
    <div
      className={classNames([styles.select, showAll ? styles.showAll : null])}
    >
      {renderChildren}
      {computedChildren}
      <When condition={showToggle && categories.length > 0}>
        <div
          className={classNames([styles.item, styles.down])}
          style={{ marginRight: right }}
          onClick={onToggle}
        >
          <HBIcon type="ic_fold" rotate={180} />
        </div>
      </When>
    </div>
  );
}
